import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Music, Users, RefreshCw, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const META = {
  creator: { label: "Criador de Eventos", icon: CalendarPlus, desc: "Criar eventos, contratar músicos e gerenciar equipes." },
  freelancer: { label: "Músico Freelancer", icon: Music, desc: "Encontrar eventos, receber convites e participar de oportunidades." },
  both: { label: "Ambos", icon: Users, desc: "Criar eventos e também participar como músico — painel unificado." },
};
const ORDER = ["creator", "freelancer", "both"];

export default function PersonaSwitcher() {
  const { user, loadUser } = useAuth();
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const current = META[user?.user_type] ? user.user_type : "freelancer";

  const change = async () => {
    setSaving(true);
    try {
      await api.post("/me/persona", { user_type: target });
      await loadUser();
      toast.success(`Experiência alterada para ${META[target].label}.`);
      setTarget(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="persona-use-section">
      <h2 className="font-head text-lg font-semibold flex items-center gap-2 mb-1"><RefreshCw className="h-5 w-5 text-accent" />Perfil de uso</h2>
      <p className="text-sm text-zinc-500 mb-4">Define o painel e os atalhos que aparecem pra você. Mudar não apaga seus dados, eventos, candidaturas ou histórico.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {ORDER.map((key) => {
          const m = META[key];
          const Icon = m.icon;
          const active = key === current;
          return (
            <button key={key} type="button" data-testid={`persona-option-${key}`}
              onClick={() => { if (!active) setTarget(key); }}
              aria-pressed={active}
              className={`text-left rounded-2xl border p-4 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                active ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-[#09090B] hover:border-primary/40"}`}>
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
                {active && <span className="text-[10px] font-bold uppercase tracking-widest text-primary flex items-center gap-1"><Check className="h-3.5 w-3.5" />Atual</span>}
              </div>
              <div className="font-semibold mt-3" data-testid={active ? "persona-current-label" : undefined}>{m.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{m.desc}</div>
            </button>
          );
        })}
      </div>

      <AlertDialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <AlertDialogContent data-testid="persona-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Alterar perfil de uso</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja alterar sua experiência no Mí Chamou para <b className="text-foreground">{target ? META[target].label : ""}</b>? Seus eventos, candidaturas, pagamentos, agenda e avaliações serão mantidos — muda apenas o painel e os atalhos exibidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="persona-switch-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="persona-switch-confirm-button" onClick={(e) => { e.preventDefault(); change(); }} disabled={saving}>
              {saving ? "Alterando…" : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
